"use client";

import { partnerHrefs } from "@/lib/usePartnerCode";
import { useActivePartnerCode, withPartnerCode } from "@/lib/useActivePartnerCode";
import Link from "@/components/LocaleLink";
import { useEffect, useState } from "react";
import { useSitePathname } from "@/lib/i18n/useSitePathname";
import { ArrowRight, Menu, X } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { WithAmp } from "@/components/WithAmp";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { AnnouncementBar } from "@/components/landing/AnnouncementBar";
import { cn } from "@/lib/utils";
import { whatsappLink, telegramLink, isSupportOpen } from "@/lib/config";
import { WhatsAppIcon, TelegramIcon } from "@/components/BrandIcons";
import { useTranslation } from "@/lib/i18n/LanguageContext";

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Whether someone is at the other end of the chat right now. null until the
  // clock has been read after mount, so the server paint never claims "open"
  // at night. Only while it is true does the WhatsApp badge breathe.
  const [erreichbar, setErreichbar] = useState<boolean | null>(null);
  const pathname = useSitePathname();
  const { t, locale } = useTranslation();
  // On a partner page (/p/lena), and on pages it passed the code to
  // (?code=), every main button and the pricing link keep the partner code.
  const partnerCode = useActivePartnerCode();
  const hrefs = partnerHrefs(partnerCode);
  const chatKicker = erreichbar
    ? t("common", "chatOpenKicker")
    : t("common", "chatClosedKicker");
  const chatTitle = erreichbar
    ? `WhatsApp · ${chatKicker}`
    : `WhatsApp · ${t("common", "chatHours")}`;
  // The link's name has to contain the words it shows ("Jetzt online" from
  // xl), or voice control cannot target it (label-content-name-mismatch).
  const chatLabel = erreichbar ? `${t("common", "chatOnline")} · WhatsApp` : "WhatsApp";

  // Every destination appears once in the header. The free check is not in
  // this list: the announcement bar above and the hero already lead there,
  // and a third entry under yet another name confused visitors. Telegram and
  // e-mail lives on the contact page, which is the last item here.
  const navLinks = [
    { href: "/preise", label: t("common", "navPricing") },
    { href: "/ratgeber", label: t("common", "navRatgeber") },
    { href: "/ueber-uns", label: t("common", "navAbout") },
    { href: "/testimonials", label: t("common", "navExperiences") },
    { href: "/kontakt", label: t("common", "navContact") },
  ];
  // "You are here": the section the current page belongs to (an article counts
  // for the blog). The /en and /da prefixes are ignored.
  const basePath = (pathname ?? "/").replace(/^\/(en|da|ar|uk|ru|vi|es)(?=\/|$)/, "") || "/";
  const isCurrent = (href: string) => basePath === href || basePath.startsWith(`${href}/`);
  // Checkout, check and inquiry form: the red pills led away from the form
  // (inputs lost) or to the page itself, so they are left out there
  // (journey test 04.10.2026). The menu keeps both actions.
  const focusedFlow = ["/buchen", "/anfrage", "/eignungs-check"].includes(basePath);

  useEffect(() => {
    const pruefen = () => setErreichbar(isSupportOpen());
    pruefen();
    // A minute is precise enough for an hours boundary and costs nothing.
    const id = window.setInterval(pruefen, 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    // Floating CTAs listen for this class and hide while the menu is open,
    // otherwise they overlap the menu buttons.
    document.body.classList.toggle("nav-open", open);
    return () => {
      document.body.style.overflow = "";
      document.body.classList.remove("nav-open");
    };
  }, [open]);

  return (
    <>
      {/* The announcement bar stays static and scrolls away; only the nav is
          sticky, on a solid background. */}
      <AnnouncementBar />
      <header
        className={cn(
          // Solid: at 92 % the page text showed through behind the wordmark
          // while scrolling (journey test 11.10.2026).
          "sticky top-0 z-50 border-b border-navy-100/70 bg-cream-50 transition-shadow duration-300",
          scrolled && "shadow-[var(--shadow-soft)]",
        )}
      >
        {/* Desktop: both rows share one grid [1fr | auto | 1fr] (subgrid). The
            middle column is as wide as the navigation, so the wordmark stands
            exactly above "Preise und Services" and the navigation stays centred.
            Row one: the languages directly left of the wordmark, and on the
            right "Jetzt online", the chat badges and the two red pills. Phone and tablet: burger
            left, wordmark centred, chat right. */}
        <div className="mx-auto max-w-7xl lg:grid lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:px-8">
        <div className="relative flex items-center justify-between gap-3 px-2 py-2.5 sm:px-4 lg:col-span-3 lg:grid lg:h-[70px] lg:grid-cols-subgrid lg:gap-0 lg:px-0 lg:py-0">
          <div className="flex items-center lg:me-[62px] lg:justify-self-end">
            <button
              type="button"
              aria-label={open ? t("common", "menuClose") : t("common", "menuOpen")}
              aria-expanded={open}
              className="grid h-11 w-11 place-items-center text-navy-800 transition-colors hover:text-coral-600 lg:hidden"
              onClick={() => setOpen((value) => !value)}
            >
              {open ? (
                <X className="h-[22px] w-[22px]" strokeWidth={1.6} />
              ) : (
                <Menu className="h-[22px] w-[22px]" strokeWidth={1.6} />
              )}
            </button>
            {/* On desktop the languages take the slot the burger leaves,
                right beside the wordmark: 62px, which under the desktop
                zoom (.9) is the same 56px as the gap from the wordmark to
                "Jetzt online" on the right. */}
            <LanguageSwitcher layout="menu" className="hidden lg:block" />
          </div>

          {/* On a phone the wordmark centres in the space between the burger
              and the chat badges; on a tablet it is pinned to the page axis.
              On desktop it starts where the navigation starts. */}
          <Link
            href="/"
            prefetch
            className="mx-auto flex min-w-0 items-center sm:absolute sm:left-1/2 sm:top-1/2 sm:mx-0 sm:-translate-x-1/2 sm:-translate-y-1/2 lg:static lg:mx-0 lg:translate-x-0 lg:translate-y-0 lg:justify-self-start"
            onClick={(e) => {
              if (pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            <BrandLogo variant="dark" />
          </Link>

          {/* w-max: the group keeps its natural width and may reach into the
              middle column; squeezed into its own column it wrapped
              "Jetzt online" and pressed the WhatsApp badge flat. */}
          <div className="flex items-center gap-2 lg:w-max lg:justify-self-end">
            {/* The two chat channels as their round app icons, in the brands'
                own colours — the marks people tap on their phones every day.
                While someone is actually there (SUPPORT_HOURS) the WhatsApp
                badge breathes a green halo and, where there is room, says so
                in words. Outside the hours both stay still. On a phone each
                link gets a 44px tap area through padding that a negative
                margin cancels, so the badges keep their place. */}
            <a
              href={whatsappLink(undefined, locale, partnerCode)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={chatLabel}
              title={chatTitle}
              data-track="cta_header_whatsapp"
              data-track-location="header_top"
              className="group -m-1 inline-flex items-center gap-3 p-1 sm:m-0"
            >
              {/* RU/UK: the two red pills are so long that the words ran
                  into the wordmark (04.10.2026); the breathing halo still
                  says "online". */}
              {erreichbar && locale !== "ru" && locale !== "uk" && (
                <span className="hidden whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.16em] text-[#075E54] xl:inline">
                  {t("common", "chatOnline")}
                </span>
              )}
              <span
                className={cn(
                  "grid h-9 w-9 place-items-center rounded-full bg-[#25D366] text-white shadow-[0_6px_14px_-6px_rgba(18,140,126,0.7)] transition-transform duration-200 group-hover:-translate-y-0.5",
                  erreichbar && "live-ring",
                )}
              >
                <WhatsAppIcon mono className="h-5 w-5" />
              </span>
            </a>
            <a
              href={telegramLink()}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Telegram"
              title="Telegram"
              data-track="cta_header_telegram"
              data-track-location="header_top"
              className="group -m-1 hidden items-center p-1 min-[375px]:inline-flex sm:m-0"
            >
              <TelegramIcon
                mono
                className="h-9 w-9 text-[#229ED9] drop-shadow-[0_4px_5px_rgba(34,158,217,0.25)] transition-transform duration-200 group-hover:-translate-y-0.5"
              />
            </a>

            {/* The free check beside the booking, in the same red pill with the
                arrow (operator, 28.09.2026: "buchen und separat noch Check …
                auch rot wie Jetzt buchen und mit Pfeil nach rechts"). */}
            <Link
              href={hrefs.check}
              prefetch
              data-track="cta_header_check"
              data-track-location="header_top"
              className={cn(
                "group ms-3 hidden h-10 items-center gap-2 whitespace-nowrap rounded-full bg-coral-500 px-4 text-[14.5px] font-semibold text-white shadow-[0_8px_18px_-8px_rgba(200,16,46,0.7)] transition hover:-translate-y-px hover:bg-coral-600",
                !focusedFlow && "lg:inline-flex",
              )}
            >
              {locale === "ru" || locale === "uk" ? (
                // Long words: the short label below xl, or the pill
                // pressed the chat badges against the wordmark.
                <>
                  <span className="xl:hidden">{t("dock", "checkShort")}</span>
                  <span className="hidden xl:inline">{t("dock", "checkNow")}</span>
                </>
              ) : (
                t("dock", "checkNow")
              )}
              <ArrowRight
                className="h-4 w-4 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5"
                strokeWidth={2.2}
                aria-hidden
              />
            </Link>

            {/* The header's one red action: a round pill in the flag red, in
                the same round shape language as the seal and the chat badges,
                and set in normal-case type so it reads as an invitation, not a
                stamp. */}
            <Link
              href={hrefs.book}
              prefetch
              data-track="cta_header_anfrage"
              data-track-location="header_top"
              className={cn(
                "group ms-2 hidden h-10 items-center gap-2 whitespace-nowrap rounded-full bg-coral-500 px-4 text-[14.5px] font-semibold text-white shadow-[0_8px_18px_-8px_rgba(200,16,46,0.7)] transition hover:-translate-y-px hover:bg-coral-600",
                !focusedFlow && "lg:inline-flex",
              )}
            >
              {t("common", "navBook")}
              <ArrowRight
                className="h-4 w-4 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5"
                strokeWidth={2.2}
                aria-hidden
              />
            </Link>
          </div>
        </div>

        {/* Phone and tablet: the desktop header's two red actions as one row
            under the wordmark, so they are on every screen, not only in the
            menu (operator, 28.09.2026). The label size follows the screen
            width, so both pills fit side by side down to 320px. */}
        <div className={cn("flex gap-2 px-3 pb-2.5 sm:justify-center sm:gap-3 lg:hidden", focusedFlow && "hidden")}>
          <Link
            href={hrefs.check}
            prefetch
            data-track="cta_header_check"
            data-track-location="header_mobile"
            className="group inline-flex h-10 min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-coral-500 px-3 text-[clamp(12.5px,3.6vw,14.5px)] font-semibold text-white shadow-[0_8px_18px_-8px_rgba(200,16,46,0.7)] transition active:scale-[0.98] sm:flex-none sm:px-5"
          >
            {t("dock", "checkShort")}
            <ArrowRight className="h-4 w-4 shrink-0 max-[374px]:hidden" strokeWidth={2.2} aria-hidden />
          </Link>
          <Link
            href={hrefs.book}
            prefetch
            data-track="cta_header_anfrage"
            data-track-location="header_mobile"
            className="group inline-flex h-10 min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-coral-500 px-3 text-[clamp(12.5px,3.6vw,14.5px)] font-semibold text-white shadow-[0_8px_18px_-8px_rgba(200,16,46,0.7)] transition active:scale-[0.98] sm:flex-none sm:px-5"
          >
            {t("common", "navBook")}
            <ArrowRight className="h-4 w-4 shrink-0 max-[374px]:hidden" strokeWidth={2.2} aria-hidden />
          </Link>
        </div>

        {/* Row two: the navigation in the middle column, centred on the page;
            the hairline above it runs across the whole header. */}
        <div className="hidden lg:col-span-3 lg:grid lg:grid-cols-subgrid">
          <nav
            className="flex items-center gap-8 py-2.5 lg:col-start-2 xl:gap-10"
            aria-label={t("common", "mainNav")}
          >
            {navLinks.map((item) => {
              const current = isCurrent(item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href === "/preise" ? withPartnerCode(item.href, partnerCode) : item.href}
                  prefetch
                  aria-current={current ? "page" : undefined}
                  // The current section in wine with its underline kept
                  // drawn (the hover underline of .editorial-link, whose
                  // unlayered scaleX(0) needs the important flag).
                  className={cn(
                    "editorial-link whitespace-nowrap text-[12.5px] font-semibold uppercase transition-colors hover:text-danish-600",
                    current ? "text-danish-700 after:[transform:scaleX(1)]!" : "text-navy-700",
                  )}
                >
                  <WithAmp text={item.label} />
                </Link>
              );
            })}
          </nav>
        </div>
        </div>
        <span aria-hidden className="pointer-events-none absolute inset-x-0 top-[70px] hidden h-px bg-navy-100/70 lg:block" />
      </header>

      <div
        aria-hidden={!open}
        // `inert` removes the closed menu from the tab order and the a11y tree;
        // aria-hidden alone would leave the links focusable (WCAG
        // aria-hidden-focus) while keeping the slide animation.
        inert={!open}
        // Must outrank the header's z-50, otherwise the sticky header renders
        // on top of the opened menu.
        className={cn(
          "fixed inset-0 z-[60] lg:hidden",
          open ? "pointer-events-auto" : "pointer-events-none",
        )}
      >
        <button
          type="button"
          aria-label={t("common", "menuClose")}
          onClick={() => setOpen(false)}
          className={cn(
            "absolute inset-0 bg-navy-900/25 backdrop-blur-sm transition-opacity duration-300",
            open ? "opacity-100" : "opacity-0",
          )}
        />

        <aside
          className={cn(
            "absolute end-0 top-0 flex h-full w-[88%] max-w-sm flex-col border-s border-navy-100 bg-cream-50 transition-transform duration-300",
            // Shadow only while open: parked off-screen, its shadow still
            // showed as a faint grey strip along the right edge of every
            // phone screen.
            // Right to left the panel sits at the left edge and parks off-screen there.
            open ? "translate-x-0 shadow-[var(--shadow-premium)]" : "translate-x-full rtl:-translate-x-full",
          )}
        >
          {/* Short screens (iPhone SE, any phone with the browser bars out):
              every row a little tighter, so all five links, both buttons and
              the chats fit without scrolling — "Kontakt" was cut off at
              375×667 (journey test 04.10.2026). */}
          <div className="flex items-center justify-between border-b border-navy-100 py-3 ps-5 pe-2 [@media(max-height:760px)]:py-1.5">
            <Link
              href="/"
              prefetch
              className="flex min-w-0 items-center"
              onClick={() => setOpen(false)}
            >
              <BrandLogo variant="dark" />
            </Link>
            <button
              type="button"
              aria-label={t("common", "close")}
              onClick={() => setOpen(false)}
              className="grid h-11 w-11 place-items-center text-navy-800 transition-colors hover:text-coral-600"
            >
              <X className="h-[22px] w-[22px]" strokeWidth={1.6} />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-5 py-3 [@media(max-height:760px)]:py-1.5">
            {/* Language first: on a phone this menu is the one place to switch,
                and below the links it went unseen (operator, 29.09.2026). One
                row that opens the seven languages, so the menu links stay in
                view without scrolling (operator, 03.10.2026). */}
            <div className="border-b border-navy-100 pb-3 pt-1 [@media(max-height:760px)]:pb-2 [@media(max-height:760px)]:pt-0">
              <LanguageSwitcher layout="compact" />
            </div>
            <ul className="divide-y divide-navy-100">
              {navLinks.map((item) => {
                const current = isCurrent(item.href);
                return (
                  <li key={item.href}>
                    <Link
                      href={item.href === "/preise" ? withPartnerCode(item.href, partnerCode) : item.href}
                      onClick={() => setOpen(false)}
                      aria-current={current ? "page" : undefined}
                      className={cn(
                        "flex items-center justify-between py-4 font-display text-xl font-semibold transition-colors hover:text-danish-600 [@media(max-height:760px)]:py-2.5 [@media(max-height:760px)]:text-lg",
                        current ? "text-danish-700" : "text-navy-900",
                      )}
                    >
                      <WithAmp text={item.label} />
                      <ArrowRight className="h-4 w-4 text-navy-400" strokeWidth={1.7} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* The two actions, then the two chat channels — the same things the
              desktop header carries, nothing more. */}
          <div className="space-y-3 border-t border-navy-100 p-5 dock-safe-bottom [@media(max-height:760px)]:space-y-2 [@media(max-height:760px)]:pt-3">
            <Link
              href={hrefs.check}
              onClick={() => setOpen(false)}
              data-track="cta_header_check"
              data-track-location="mobile_menu"
              className="flex h-12 items-center justify-center gap-2 rounded-full bg-coral-500 px-4 text-[15px] font-semibold text-white shadow-[0_8px_18px_-8px_rgba(200,16,46,0.7)] transition-colors active:bg-coral-600 [@media(max-height:760px)]:h-11"
            >
              {t("dock", "checkNow")}
              <ArrowRight className="h-4 w-4" strokeWidth={2.2} aria-hidden />
            </Link>
            <Link
              href={hrefs.book}
              onClick={() => setOpen(false)}
              data-track="cta_header_anfrage"
              data-track-location="mobile_menu"
              className="flex h-12 items-center justify-center gap-2 rounded-full bg-coral-500 px-4 text-[15px] font-semibold text-white shadow-[0_8px_18px_-8px_rgba(200,16,46,0.7)] transition-colors active:bg-coral-600 [@media(max-height:760px)]:h-11"
            >
              {t("common", "navBook")}
              <ArrowRight className="h-4 w-4" strokeWidth={2.2} aria-hidden />
            </Link>
            <div className="grid grid-cols-2 gap-2.5">
              <a
                href={whatsappLink(undefined, locale, partnerCode)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-track="cta_header_whatsapp"
                data-track-location="mobile_menu"
                className="flex h-12 items-center justify-center gap-2.5 rounded-full border border-navy-100 bg-white text-[13px] font-semibold text-navy-800 transition-colors hover:border-[#25D366] [@media(max-height:760px)]:h-11"
              >
                <span
                  className={cn(
                    "grid h-7 w-7 place-items-center rounded-full bg-[#25D366] text-white",
                    erreichbar && "live-ring",
                  )}
                >
                  <WhatsAppIcon mono className="h-4 w-4" />
                </span>
                WhatsApp
              </a>
              <a
                href={telegramLink()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-track="cta_header_telegram"
                data-track-location="mobile_menu"
                className="flex h-12 items-center justify-center gap-2.5 rounded-full border border-navy-100 bg-white text-[13px] font-semibold text-navy-800 transition-colors hover:border-[#229ED9] [@media(max-height:760px)]:h-11"
              >
                <TelegramIcon mono className="h-7 w-7 text-[#229ED9]" />
                Telegram
              </a>
            </div>
            <p className="flex items-center justify-center gap-2 text-[12px] font-semibold text-navy-600 [@media(max-height:620px)]:hidden">
              <span
                aria-hidden
                className={cn("h-2 w-2 rounded-full", erreichbar ? "bg-[#25D366]" : "bg-navy-300")}
              />
              {erreichbar === null ? t("common", "chatHours") : chatKicker}
            </p>
          </div>
        </aside>
      </div>
    </>
  );
}
